import React, { useState, useEffect } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import axios from "axios";
import * as moment from "moment";
import DatePicker from "react-multi-date-picker";
import {
  getDatePickerFormat,
  getDateFormat,
  getCalendarLocale,
} from "@utils/datePickerUtils";
import { Dialog, Button, TextInput, Image } from "hesus-ui-kit";
import ClipLoader from "react-spinners/ClipLoader";
import HolderCompany from "./HolderCompany";
import {
  labelStyles,
  inputStyles,
  labelStylesError,
  inputStylesError,
  RequiredMessagePara,
} from "@utils/extra-style";
import {
  HeaderWrapper,
  Title,
  ButtonWrapper,
  FormBody,
  OptionsWrapper,
  FlexRow,
  SectionContainer,
  GridRow,
  ContainerLabel,
} from "./AddEditClient.styled";
import "./AddEditClient.scss";
import closeIcon from "@icons/ic-close-white.svg";
import enableIcon from "@icons/ic-enable.svg";
import disableIcon from "@icons/ic-disable.svg";
import CKEditorText from "@components/CKEditorText/CKEditorText";

const AddEditClient = (props) => {
  const { t, onClose, updateRefeshStatus, company } = props;
  const [isAccountCreating, setIsAccountCreating] = useState(false);
  const [createAccountAPIError, setCreateAccountAPIError] = useState("");
  const [isEditStep, setIsEditStep] = useState(
    !!company.creditLimit?.hasCreditLimitHolder
  );
  const [financialComment, setFinancialComment] = useState(
    company.financialComment ?? ""
  );

  const [formData, setFormData] = useState({
    vatNumber: {
      required: true,
      value: company.intracomVatNumber ?? "",
      error: "",
    },
    isCreditHolder: {
      required: true,
      value: company.creditLimit?.hasCreditLimitHolder === 1 ? 1 : 2,
      error: "",
    },
    creditAmount: {
      required: true,
      value: company.creditLimit?.amount,
      error: "",
    },
    creditExpirationDate: {
      required: true,
      value: company.creditLimit?.expirationDate || "",
      error: "",
    },
    hesusCoverAmount: {
      value: company.hesusCover?.amount,
      error: "",
      required: true,
    },
    hesusCoverExpirationDate: {
      value: company.hesusCover?.expirationDate || "",
      error: "",
      required: true,
    },
    financialComment: {
      value: company.financialComment ?? "",
      error: "",
      required: true,
    },
    company: {
      required: true,
      value:
        {
          id: company.creditLimit?.companyId,
          name: company.creditLimit?.companyName,
        } ?? "",
      error: "",
    },
  });

  const onVatNumberChange = (e) => {
    setFormData({
      ...formData,
      vatNumber: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const onCreditHolderChange = () => {
    setFormData({
      ...formData,
      isCreditHolder: {
        value: formData.isCreditHolder.value === 1 ? 2 : 1,
        error: "",
        required: true,
      },
      creditAmount: {
        value: "",
        error: "",
        required: true,
      },
      creditExpirationDate: {
        value: "",
        error: "",
        required: true,
      },
    });
    setIsEditStep(!isEditStep);
  };

  const onCreditAmountChange = (e) => {
    setFormData({
      ...formData,
      creditAmount: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const onCreditExpirationDateChange = (e) => {
    setFormData({
      ...formData,
      creditExpirationDate: {
        value: moment(e.toDate()).format("YYYY-MM-DD"),
        error: "",
        required: true,
      },
    });
  };

  const onHesusCoverAmountChange = (e) => {
    setFormData({
      ...formData,
      hesusCoverAmount: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const onHesusCoverExpirationDateChange = (e) => {
    setFormData({
      ...formData,
      hesusCoverExpirationDate: {
        value: moment(e.toDate()).format("YYYY-MM-DD"),
        error: "",
        required: true,
      },
    });
  };

  useEffect(() => {
    setFormData({
      ...formData,
      financialComment: {
        value: financialComment,
        error: "",
        required: true,
      },
    });
  }, [financialComment]);

  const validateForm = (data) => {
    let isFormValid = true;
    if (data.isCreditHolder.value === 1) {
      if (!data.company?.value?.id) {
        isFormValid = false;
        data.company.error = t("COMPANY_CREDIT_LIMIT_HOLDER_ERROR");
      }
    }
    if (data.isCreditHolder.value !== 1) {
      if (!data.creditAmount.value && data.creditAmount.value !== 0) {
        isFormValid = false;
        data.creditAmount.error = t("GENERAL_MANDATORY_ERROR");
      }
      if (data.creditAmount.value && !/^\d+$/.test(data.creditAmount.value)) {
        isFormValid = false;
        data.creditAmount.error = t("INVALID_FORMAT");
      }
      if (!data.creditExpirationDate.value) {
        isFormValid = false;
        data.creditExpirationDate.error = t("GENERAL_MANDATORY_ERROR");
      }
    }
    if (
      data.hesusCoverAmount.value &&
      !/^\d+$/.test(data.hesusCoverAmount.value)
    ) {
      isFormValid = false;
      data.hesusCoverAmount.error = t("INVALID_FORMAT");
    }

    if (!isFormValid) {
      setFormData({ ...data });
    }

    return isFormValid;
  };

  const getFormData = () => {
    const data = {
      id: company.id,
      name: company.name,
      registrationNumber: company.registrationNumber,
      platformCountryId: company.platformCountryId,
      activity: { id: company.activity.id },
      intracomVatNumber: formData.vatNumber.value,
      website: company.website,
      comment: company.comment,
      shippingAddress: {
        address: company.shippingAddress.address,
        zipcode: company.shippingAddress.postalCode,
        city: company.shippingAddress.city,
        country: company.shippingAddress.country,
        supplement: company.shippingAddress.supplement,
        position: {
          latitude: company.shippingAddress.position.latitude,
          longitude: company.shippingAddress.position.longitude,
        },
      },
      billingAddress: {
        address: company.billingAddress.address,
        zipcode: company.billingAddress.postalCode,
        city: company.billingAddress.city,
        country: company.billingAddress.country,
        supplement: company.billingAddress.supplement,
        position: {
          latitude: company.billingAddress.position.latitude,
          longitude: company.billingAddress.position.longitude,
        },
      },
      isClient: company.isClient,
      isSupplier: company.isSupplier,
      isHaulier: company.isHaulier,
      isCentre: company.isCentre,
      parentCompany: company?.parentCompany?.id
        ? { id: company.parentCompany.id }
        : "",
      phone: company.phone,
      boxFolderId: company.boxFolderId,
      creditLimit: {
        hasCreditLimitHolder: formData?.isCreditHolder?.value === 1,
        companyId: formData?.company?.value?.id,
        companyName: formData.company.value?.name,
        amount: formData.creditAmount.value,
        expirationDate: formData.creditExpirationDate.value
          ? moment(formData.creditExpirationDate.value).format("YYYY-MM-DD")
          : "",
      },
      hesusCover: {
        amount: formData.hesusCoverAmount.value,
        expirationDate: formData.hesusCoverExpirationDate.value
          ? moment(formData.hesusCoverExpirationDate.value).format("YYYY-MM-DD")
          : "",
      },
      financialComment: formData.financialComment.value,
    };
    return data;
  };

  const onSubmit = () => {
    const isFormValid = validateForm(formData);
    if (isFormValid) {
      setIsAccountCreating(true);
      setCreateAccountAPIError("");
      axios
        .put(`/companies/${company.id}`, getFormData())
        .then(() => {
          setIsAccountCreating(false);
          updateRefeshStatus();
          onClose();
        })
        .catch((error) => {
          setIsAccountCreating(false);
          setCreateAccountAPIError(error.response?.data?.message ?? "");
        });
    }
  };

  const getLabelClassName = () => {
    let name = "";
    if (formData.isCreditHolder.value !== 1) {
      if (formData.creditExpirationDate.error) {
        name =
          "add-edit-client__date-container_start_error add-edit-client__date-container_start_required";
      } else {
        name =
          "add-edit-client__date-container_start add-edit-client__date-container_start_required";
      }
    }

    if (formData.isCreditHolder.value === 1) {
      name = "add-edit-client__date-container_start";
    }

    return name;
  };

  const getDateInputClasssName = () => {
    let inputClass = "";
    if (formData.isCreditHolder.value !== 1) {
      if (formData.creditExpirationDate.error) {
        inputClass = "add-edit-client__date-input-error";
      } else {
        inputClass = "add-edit-client__date-input";
      }
    }

    if (formData.isCreditHolder.value === 1) {
      inputClass =
        "add-edit-client__date-input add-edit-client__date-input-background";
    }

    return inputClass;
  };

  return (
    <Dialog width="45rem">
      <div>
        <HeaderWrapper>
          <Title>
            {company.name} {t("COMPANY_FINANCIAL_INFORMATION_FORM_TITLE")}
          </Title>
          <Button
            onClick={onClose}
            styles={{
              backgroundColor: "transparent",
              border: "none",
              height: "auto",
              padding: 0,
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            <Image
              src={closeIcon}
              alt="close Product price popup"
              styles={{ width: "auto" }}
            />
          </Button>
        </HeaderWrapper>
        <FormBody>
          <OptionsWrapper>
            <FlexRow>
              <SectionContainer>
                <div>
                  <GridRow column="1fr">
                    <TextInput
                      id="companyVatNumber"
                      type="text"
                      label={t("COMPANY_INTRACOM_VAT_NUMBER")}
                      isFloat
                      value={String(formData.vatNumber.value)}
                      onChange={(e) => onVatNumberChange(e)}
                      containerStyles={{ marginTop: "21px" }}
                      errorStyles={{ "font-style": "italic" }}
                      labelStyles={
                        formData.vatNumber.error !== ""
                          ? labelStylesError
                          : labelStyles
                      }
                      inputStyles={
                        formData.vatNumber.error !== ""
                          ? inputStylesError
                          : inputStyles
                      }
                      error={formData.vatNumber.error}
                      placeholder={t("COMPANY_INTRACOM_VAT_NUMBER")}
                    />
                  </GridRow>
                  <GridRow>
                    <div>
                      <Image
                        src={
                          formData.isCreditHolder.value === 1
                            ? enableIcon
                            : disableIcon
                        }
                        alt={t("COMPANY_HAS_CREDIT_LIMIT_HOLDER")}
                        title={t("COMPANY_HAS_CREDIT_LIMIT_HOLDER")}
                        styles={{ marginLeft: "7px" }}
                        onClick={() => onCreditHolderChange()}
                        className="add-edit-client__image-icon"
                      />
                      <label className="label add-edit-client__active-label">
                        {t("COMPANY_HAS_CREDIT_LIMIT_HOLDER")}
                      </label>
                    </div>
                  </GridRow>
                  {formData.isCreditHolder.value === 1 && (
                    <GridRow column="70% 1fr">
                      <div>
                        <HolderCompany
                          formData={formData}
                          setFormData={setFormData}
                          selectedHolderCompany={company.creditLimit}
                          objectId={company.creditLimit?.companyId}
                        />
                      </div>
                    </GridRow>
                  )}
                  <GridRow column="35% 35% 1fr">
                    <TextInput
                      id="creditLimtAmount"
                      type="number"
                      label={t("COMPANY_CREDIT_LIMIT_AMOUNT")}
                      isFloat
                      required={!isEditStep}
                      disabled={isEditStep}
                      value={String(formData.creditAmount.value)}
                      onChange={(e) => onCreditAmountChange(e)}
                      containerStyles={{ marginTop: "21px" }}
                      errorStyles={{ "font-style": "italic" }}
                      labelStyles={
                        formData.creditAmount.error !== ""
                          ? labelStylesError
                          : labelStyles
                      }
                      inputStyles={
                        formData.creditAmount.error !== ""
                          ? inputStylesError
                          : inputStyles
                      }
                      error={formData.creditAmount.error}
                      placeholder={t("COMPANY_CREDIT_LIMIT_AMOUNT")}
                    />
                    <div className={getLabelClassName()}>
                      <label htmlFor="winning-date">
                        {t("COMPANY_CREDIT_LIMIT_EXPIRATION")}
                      </label>
                      <DatePicker
                        disabled={isEditStep}
                        inputClass={getDateInputClasssName()}
                        containerClassName="add-enquiry__datepicker-container"
                        type="text"
                        label={t("COMPANY_CREDIT_LIMIT_EXPIRATION")}
                        name="add-edit-client-credit-expiration-date"
                        id="add-edit-client-credit-expiration-date"
                        calendarPosition="bottom-center"
                        isFloat
                        onChange={onCreditExpirationDateChange}
                        format={getDatePickerFormat()}
                        value={
                          getDateFormat(
                            formData.creditExpirationDate.value,
                            "YYYY-MM-DD"
                          ) || ""
                        }
                        containerStyles={{ marginTop: "21px" }}
                        labelStyles={
                          formData.creditExpirationDate.error !== ""
                            ? labelStylesError
                            : labelStyles
                        }
                        locale={getCalendarLocale()}
                        required
                        error={formData.creditExpirationDate.error}
                        placeholder={t("COMPANY_CREDIT_LIMIT_EXPIRATION")}
                      />
                      {formData.creditExpirationDate.error && (
                        <RequiredMessagePara>
                          {formData.creditExpirationDate.error}
                        </RequiredMessagePara>
                      )}
                    </div>
                  </GridRow>
                  <GridRow column="35% 35% 1fr">
                    <TextInput
                      id="hesusCoverAmount"
                      type="number"
                      label={t("COMPANY_HESUS_COVER_AMOUNT")}
                      isFloat
                      value={String(formData.hesusCoverAmount.value)}
                      onChange={(e) => onHesusCoverAmountChange(e)}
                      containerStyles={{ marginTop: "21px" }}
                      errorStyles={{ "font-style": "italic" }}
                      labelStyles={
                        formData.hesusCoverAmount.error !== ""
                          ? labelStylesError
                          : labelStyles
                      }
                      inputStyles={
                        formData.hesusCoverAmount.error !== ""
                          ? inputStylesError
                          : inputStyles
                      }
                      error={formData.hesusCoverAmount.error}
                      placeholder={t("COMPANY_HESUS_COVER_AMOUNT")}
                    />
                    <div className="add-edit-client__date-container_start">
                      <label htmlFor="hesus-cover-expiration-date">
                        {t("COMPANY_HESUS_COVER_EXPIRATION")}
                      </label>
                      <DatePicker
                        inputClass="add-edit-client__date-input"
                        containerClassName="add-enquiry__datepicker-container"
                        type="text"
                        label={t("COMPANY_HESUS_COVER_EXPIRATION")}
                        name="add-edit-client-credit-expiration-date"
                        id="add-edit-client-credit-expiration-date"
                        calendarPosition="bottom-center"
                        isFloat
                        onChange={onHesusCoverExpirationDateChange}
                        format={getDatePickerFormat()}
                        locale={getCalendarLocale()}
                        value={
                          getDateFormat(
                            formData.hesusCoverExpirationDate.value,
                            "YYYY-MM-DD"
                          ) || ""
                        }
                        containerStyles={{ marginTop: "21px" }}
                        labelStyles={
                          formData.hesusCoverExpirationDate.error !== ""
                            ? labelStylesError
                            : labelStyles
                        }
                        required
                        error={formData.hesusCoverExpirationDate.error}
                        placeholder={t("COMPANY_HESUS_COVER_EXPIRATION")}
                      />
                    </div>
                  </GridRow>
                  <GridRow column="1fr">
                    <ContainerLabel>
                      {t("COMPANY_FINANCIAL_COMMENT")}
                    </ContainerLabel>
                  </GridRow>
                  <GridRow column="1fr">
                    <CKEditorText
                      descriptionData={financialComment}
                      setDescriptionData={setFinancialComment}
                    />
                    {formData.financialComment.error && (
                      <RequiredMessagePara>
                        {formData.financialComment.error}
                      </RequiredMessagePara>
                    )}
                  </GridRow>
                </div>
              </SectionContainer>
            </FlexRow>
          </OptionsWrapper>
        </FormBody>

        <ButtonWrapper>
          {createAccountAPIError && <div>{createAccountAPIError}</div>}
          <Button
            styles={{
              color: "white",
              background: "#1BACE4",
              border: "1px solid #1BACE4",
              marginRight: "0.32rem",
              "&:hover": {
                backgroundColor: "#1589b6",
              },
            }}
            onClick={onSubmit}
          >
            <>
              {isAccountCreating && <ClipLoader size={15} color="#fff" />}
              {!isAccountCreating && t("SUBMIT")}
            </>
          </Button>

          <Button
            styles={{
              color: "#333",
              background: "#fff",
              border: "1px solid #ccc",
              "&:hover": {
                backgroundColor: "#ccc",
              },
            }}
            onClick={onClose}
          >
            {t("CLOSE")}
          </Button>
        </ButtonWrapper>
      </div>
    </Dialog>
  );
};
AddEditClient.propTypes = {
  t: PropTypes.func.isRequired,
  id: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  updateRefeshStatus: PropTypes.func.isRequired,
};

AddEditClient.defaultProps = {
  id: null,
};
export default withTranslation()(AddEditClient);
